import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual'
import { theme } from 'antd'
import { useState } from 'react'
import type { ReactNode } from 'react'

import type { ColumnSettingsItem } from '../types'

import { useStyles } from './styles'
import { useScrollMargin } from './useScrollMargin'
import { withPinnedIndex } from './utils'

const VIRTUAL_ROWS_FROM = 40
const ROW_OVERSCAN = 8

type Props = {
  rows: ColumnSettingsItem[]
  scrollElement: HTMLElement | null
  draggedKey: string | undefined
  renderRow: (item: ColumnSettingsItem, index: number) => ReactNode
}

export const ColumnSettingsRowList = ({
  rows,
  scrollElement,
  draggedKey,
  renderRow
}: Props) => {
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null)
  const isVirtual = rows.length >= VIRTUAL_ROWS_FROM
  const scrollMargin = useScrollMargin(
    isVirtual ? listElement : null,
    scrollElement
  )
  const draggedIndex = rows.findIndex(({ key }) => key === draggedKey)

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    enabled: isVirtual,
    getScrollElement: () => scrollElement,
    getItemKey: (index) => rows[index].key,
    estimateSize: () => token.controlHeight,
    overscan: ROW_OVERSCAN,
    scrollMargin,
    rangeExtractor: (range) =>
      withPinnedIndex(defaultRangeExtractor(range), draggedIndex),
    useFlushSync: false
  })

  if (!isVirtual) {
    return rows.map(renderRow)
  }

  return (
    <div
      ref={setListElement}
      className={styles.list}
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map(({ key, index, start }) => (
        <div
          key={key}
          className={styles.slot}
          style={{ transform: `translateY(${start - scrollMargin}px)` }}
        >
          {renderRow(rows[index], index)}
        </div>
      ))}
    </div>
  )
}
